/*
 * WC-MODEL-COLOURS.JS  -  the 3D model follows the choices made
 * ---------------------------------------------------------------------------
 *
 * Every product page shows the real part in the orange band (wc-stl.liquid,
 * Shopify's own <model-viewer>). The model arrives in whatever colours it
 * was exported in: the Bar Ends in black and yellow, the Gear Station in
 * black and blue. This repaints it to match the colour option(s) the
 * visitor has chosen, so a Red body with a White band is seen as a Red
 * body with a White band, from any angle, with no photograph of that pair
 * ever having been made.
 *
 * It works the way the website's own viewer does (website/js/stl-viewer.js,
 * and docs/claude/glb-models-on-the-website.md): the parts of a model are
 * grouped by MATERIAL NAME, and each colour option paints one group. That
 * is why the export rule there matters: a part is only recolourable if it
 * was exported with its own named material. All twelve models were checked
 * on 13 October 2026, and every one has its parts on separately named
 * materials.
 *
 * WHICH OPTION PAINTS WHICH GROUP
 * Decided in GROUPS below, per product handle: an option name, and a
 * pattern matched against the material names of the parts it paints.
 * The first guess for each product is from the exported colours (the
 * Gear Station's structure is black and its accents blue; the Bar Ends'
 * hex body is the larger group); if a product paints the wrong part, swap
 * the two patterns here. A product not listed is painted by its single
 * colour option, if it has one, over every material whose name carries a
 * colour word; materials named for glass, wood, metal or threads are never
 * touched, same as on the website.
 *
 * THE COLOURS are the website's six (colourMap in data/site-settings.js),
 * because they are what the website's viewer paints with and they look
 * like the filament; Shopify's swatch colours are flatter and are for the
 * circles, not the model.
 *
 * HOW IT TALKS TO THE MODEL
 * <model-viewer> exposes the scene: viewer.model.materials, each with a
 * name and pbrMetallicRoughness.setBaseColorFactor([r, g, b, 1]). It is
 * available once the viewer has fired "load". The colour options are the
 * radio inputs Horizon's variant picker renders (data-option-name holds the
 * option name, value the colour), and a change on any of them repaints.
 *
 * If anything is missing - no model on the page, a viewer version without
 * the scene API, a material with no colour word - it does nothing. The
 * model is then shown as exported, exactly as before this file existed.
 */
(function () {
  'use strict';

  /* The filament's own colours: Bambu Lab's published hex codes for PETG
     Basic, the filament every one of these products is printed in
     (20 October 2026). The same six are on the swatches (Shopify's Color
     entries and wc-swatch-tips.js) and on whencreate.com (colourMap in
     website/data/site-settings.js); change all four together. */
  var HEX = {
    'Black':  '#000000',
    'White':  '#FFFFFF',
    'Blue':   '#001489',
    'Orange': '#FF671F',
    'Yellow': '#FCE300',
    'Red':    '#D6001C'
  };

  /* Per product: which option paints which material names. Order matters
     only where a material would match both; the first wins. */
  var GROUPS = {
    'rev-layer-bar-ends': [
      { option: 'Body Colour', match: /black|white/i },
      { option: 'Band Colour', match: /yellow|red/i }
    ],
    'rev-layer-gear-station': [
      { option: 'Primary Colour',   match: /black/i },
      { option: 'Secondary Colour', match: /blue/i }
    ]
  };

  /* Never painted: not a choice anybody makes. Same list as the website. */
  var FIXED = /\b(glass|acrylic|perspex|clear|transparent|translucent|thread|threaded|screw|bolt|nut|washer|insert|magnet|bearing|rubber|steel|stainless|chrome|alumini?um|brass|copper|oak|wood|walnut|pine|birch|veneer)\b/i;
  var COLOUR_WORD = /\b(black|white|blue|orange|yellow|red|grey|gray|green|silver|gold|color|colour)\b/i;

  /* setBaseColorFactor takes an ARRAY in linear light, as glTF stores it;
     only a CSS string is converted from sRGB for you. A hex code is sRGB,
     so each channel is converted here. Until 20 October the sRGB values
     went in as if they were linear, which is why every repainted part
     looked paler than its colour (the "pale sky-blue" Blue of 15
     October). */
  function lin(c) {
    c = c / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function rgb(hex) {
    var n = parseInt(hex.slice(1), 16);
    return [lin((n >> 16) & 255), lin((n >> 8) & 255), lin(n & 255), 1];
  }

  function viewer() {
    return document.querySelector('.wc-stl model-viewer');
  }

  function handle() {
    var picker = document.querySelector('variant-picker[data-product-handle]');
    return picker ? picker.getAttribute('data-product-handle') : '';
  }

  /* The colour chosen for each option, read from the checked radios. */
  function chosen() {
    var out = {};
    var inputs = document.querySelectorAll('variant-picker input[type="radio"]:checked');
    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].getAttribute('data-option-name') || '';
      if (/colou?r/i.test(name)) out[name] = inputs[i].value;
    }
    return out;
  }

  /* ---- WHICH MODEL: a product with several, by the model's alt text ------
     wc-stl.liquid prints every model's address with its alt text, and the
     alts describe the model in words: "Bar Ends, standard 31 mm, 3D model",
     "Gear Station helmet holder in two colours, on its hexagonal wall
     plate, 3D model". A chosen option value is broken into words (letters
     of three or more, and numbers; colour words left out), and the model
     whose alt contains the most of those words is shown. "Standard, 31 mm,
     M8" finds "standard" and "31" in the first alt and nothing in the
     second. A value whose words appear in no alt (a colour, a finish, a
     height nobody named) changes nothing. tools/model-alt.js reports which
     value each model answers to. The viewer's src is swapped, model-viewer
     reloads, "load" fires and the paint runs again on the new model. */
  function models() {
    var el = document.querySelector('[data-wc-models]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function words(text) {
    var out = [], m, re = /[a-z]{3,}|\d+/g, s = String(text || '').toLowerCase();
    while ((m = re.exec(s))) { if (!COLOUR_WORD.test(m[0])) out.push(m[0]); }
    return out;
  }

  function switchModel() {
    var mv = viewer();
    var list = models();
    if (!mv || list.length < 2) return;
    var values = [];
    var inputs = document.querySelectorAll('variant-picker input[type="radio"]:checked');
    for (var i = 0; i < inputs.length; i++) values.push(inputs[i].value);
    var selects = document.querySelectorAll('variant-picker select');
    for (var s = 0; s < selects.length; s++) values.push(selects[s].value);
    var pick = null, best = 0;
    for (var m = 0; m < list.length; m++) {
      if (!list[m].glb) continue;
      var altWords = words(list[m].alt);
      var score = 0;
      for (var v = 0; v < values.length; v++) {
        var ws = words(values[v]);
        for (var w = 0; w < ws.length; w++) { if (altWords.indexOf(ws[w]) !== -1) score++; }
      }
      if (score > best) { best = score; pick = list[m]; }
    }
    if (!pick) return;
    if (mv.getAttribute('src') !== pick.glb) {
      mv.setAttribute('src', pick.glb);
      if (pick.usdz) mv.setAttribute('ios-src', pick.usdz);
    }
  }

  function paint() {
    var mv = viewer();
    if (!mv || !mv.model || !mv.model.materials) return;
    var picks = chosen();
    var names = Object.keys(picks);
    if (!names.length) return;

    var groups = GROUPS[handle()];
    if (!groups) {
      /* One colour option paints every material that is not fixed, whatever
         it was called: the organiser's one material is "Appearance", the
         hangers' is "color". */
      if (names.length !== 1) return;
      groups = [{ option: names[0], match: /./ }];
    }

    var materials = mv.model.materials;
    for (var m = 0; m < materials.length; m++) {
      var mat = materials[m];
      var mname = mat.name || '';
      if (FIXED.test(mname)) continue;
      for (var g = 0; g < groups.length; g++) {
        if (!groups[g].match.test(mname)) continue;
        var colour = HEX[picks[groups[g].option]];
        if (!colour) break;
        try {
          var pbr = mat.pbrMetallicRoughness;
          if (pbr.baseColorTexture && pbr.baseColorTexture.texture) {
            /* A flat colour under a texture would only tint it. */
            pbr.baseColorTexture.setTexture(null);
          }
          pbr.setBaseColorFactor(rgb(colour));
          /* MATTE, LIKE THE FILAMENT. The exported materials carry a shine
             that the viewer's lighting bleaches: a Blue body came out pale
             sky-blue on the live page (15 October). Printed PETG is not
             shiny, so a painted part gets no metal and a soft roughness,
             and the colour reads as the colour chosen. */
          if (pbr.metallicRoughnessTexture && pbr.metallicRoughnessTexture.texture) {
            pbr.metallicRoughnessTexture.setTexture(null);
          }
          pbr.setMetallicFactor(0);
          pbr.setRoughnessFactor(0.65);
        } catch (e) { /* an older viewer without the scene API: leave it */ }
        break;
      }
    }
  }

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; switchModel(); paint(); });
  }

  function wire() {
    var mv = viewer();
    if (!mv || mv.hasAttribute('data-wc-colours')) return;
    mv.setAttribute('data-wc-colours', '');
    mv.addEventListener('load', schedule);
    if (mv.model) schedule();
  }

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches('variant-picker input[type="radio"]')) schedule();
  });

  function start() {
    wire();
    /* The viewer is created by Shopify's script after the page is parsed,
       and the picker is redrawn on every choice. */
    new MutationObserver(function () { wire(); schedule(); })
      .observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
